/**
 * useWorkforceApi – hooks for workforce listings and applications.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { fetchCounterpartyShopNames } from '@/hooks/useConnectApi';

// ---------- Types ----------

export interface WorkforceListing {
  id: string;
  shop_id: string;
  title: string;
  role_target: string;
  description: string | null;
  paid: boolean;
  location_zip: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  shops?: { name: string; address: string | null; location_zip: string | null } | null;
}

/**
 * A row of the open-opportunities directory (`connect_open_listings()`, Core-100 R4 / OD-15). Shop rows are not
 * readable across tenants, so the directory carries the published listing fields plus the shop's name itself.
 */
export interface OpenWorkforceListing {
  id: string;
  shop_id: string;
  shop_name: string;
  title: string;
  role_target: string;
  description: string | null;
  paid: boolean;
  location_zip: string | null;
  created_at: string;
}

export interface WorkforceApplication {
  id: string;
  listing_id: string;
  user_id: string;
  status: string;
  created_at: string;
  updated_at: string;
  workforce_listings?: WorkforceListing | null;
  passport_profiles?: { display_name: string | null; roles: string[] } | null;
}

// ---------- Student: Browse active listings ----------

/**
 * Open opportunities. Core-100 R4 (OD-15): the directory is an RPC with the Connect audience — a person who is
 * discoverable on Connect — so `enabled` is false until they are (the server refuses anyone else).
 */
export function useWorkforceListings(discoverable: boolean) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['workforce-listings', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('connect_open_listings' as never);
      if (error) throw error;
      return ((data ?? []) as unknown) as OpenWorkforceListing[];
    },
    enabled: !!user?.id && discoverable,
  });
}

// ---------- Student: My applications ----------

export function useMyApplications() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['my-workforce-applications', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workforce_applications')
        .select('*, workforce_listings(id, title, shop_id)')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      // Shop rows are not readable across tenants (R2-12); the applied-to shop's name comes from the relationship.
      const apps = (data ?? []) as WorkforceApplication[];
      const names = await fetchCounterpartyShopNames(
        apps.map((a) => a.workforce_listings?.shop_id).filter((x): x is string => !!x),
      );
      return apps.map((a) => a.workforce_listings
        ? { ...a, workforce_listings: { ...a.workforce_listings,
            shops: names[a.workforce_listings.shop_id] ? { name: names[a.workforce_listings.shop_id], address: null, location_zip: null } : null } }
        : a);
    },
    enabled: !!user?.id,
  });
}

// ---------- Student: Apply to listing ----------

export function useApplyToListing() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (listingId: string) => {
      const { data, error } = await supabase
        .from('workforce_applications')
        .insert({ listing_id: listingId, user_id: user!.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-workforce-applications'] });
      toast({ title: 'Application submitted', description: 'The shop will review your application.' });
    },
    onError: (err: any) => {
      const msg = err?.message?.includes('duplicate')
        ? 'You have already applied to this listing.'
        : err?.message || 'Failed to submit application.';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    },
  });
}

// ---------- Student: Withdraw an application ----------

/**
 * Core-100 R4 W2 (CORE-Q): "Recruiting projection is the person's decision, and withdrawal closes it." Withdrawing is
 * the applicant's own act, through the server (`withdraw_workforce_application`); it ends the shop's read of the
 * applicant's Passport (the stated `applicant` basis) and is final. Withdrawing twice is harmless.
 */
export function useWithdrawApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (applicationId: string) => {
      const { data, error } = await supabase.rpc('withdraw_workforce_application' as never, { p_application_id: applicationId } as never);
      if (error) throw error;
      return data as unknown as 'WITHDRAWN' | 'ALREADY_WITHDRAWN';
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-workforce-applications'] });
      qc.invalidateQueries({ queryKey: ['passport-audience'] });
      toast({ title: 'Application withdrawn', description: 'The shop can no longer see your Passport through this application.' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err?.message || 'Failed to withdraw the application.', variant: 'destructive' });
    },
  });
}

// ---------- Shop: Own listings (all statuses) ----------

export function useShopListings(shopId: string | undefined) {
  return useQuery({
    queryKey: ['shop-workforce-listings', shopId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workforce_listings')
        .select('*')
        .eq('shop_id', shopId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as WorkforceListing[];
    },
    enabled: !!shopId,
  });
}

// ---------- Shop: Create listing ----------

export function useCreateListing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      shop_id: string;
      title: string;
      role_target: string;
      description?: string;
      paid?: boolean;
      location_zip?: string;
    }) => {
      const { data, error } = await supabase
        .from('workforce_listings')
        .insert(body)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['shop-workforce-listings', data.shop_id] });
      toast({ title: 'Listing created' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err?.message || 'Failed to create listing.', variant: 'destructive' });
    },
  });
}

// ---------- Shop: Update listing ----------

export function useUpdateListing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: {
      id: string;
      title?: string;
      role_target?: string;
      description?: string;
      paid?: boolean;
      location_zip?: string;
      is_active?: boolean;
    }) => {
      const { data, error } = await supabase
        .from('workforce_listings')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['shop-workforce-listings', data.shop_id] });
      qc.invalidateQueries({ queryKey: ['workforce-listings'] });
      toast({ title: 'Listing updated' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err?.message || 'Failed to update listing.', variant: 'destructive' });
    },
  });
}

// ---------- Shop: View applicants for a listing ----------

export function useListingApplicants(listingId: string | undefined) {
  return useQuery({
    queryKey: ['listing-applicants', listingId],
    queryFn: async () => {
      const { data: apps, error } = await supabase
        .from('workforce_applications')
        .select('*')
        .eq('listing_id', listingId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      if (!apps?.length) return [] as (WorkforceApplication & { applicant_name: string })[];

      // Fetch passport profiles for applicant user_ids
      const userIds = apps.map(a => a.user_id);
      const { data: profiles } = await supabase
        .from('passport_profiles')
        .select('user_id, display_name, roles')
        .in('user_id', userIds);

      const profileMap = new Map((profiles ?? []).map(p => [p.user_id, p]));
      return apps.map(a => ({
        ...a,
        applicant_name: profileMap.get(a.user_id)?.display_name ?? 'Unknown',
        applicant_roles: profileMap.get(a.user_id)?.roles ?? [],
      }));
    },
    enabled: !!listingId,
  });
}

// ---------- Shop: Update application status ----------

export function useUpdateApplicationStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { data, error } = await supabase
        .from('workforce_applications')
        .update({ status })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['listing-applicants', data.listing_id] });
      toast({ title: 'Application status updated' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err?.message || 'Failed to update status.', variant: 'destructive' });
    },
  });
}
